import {useItemPickersContext} from '@github-ui/item-picker-base/ItemPickersContext'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {IconButton, type TokenProps} from '@primer/react'
import type React from 'react'
import {useCallback, useEffect, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {IssuePicker, type IssuePickerItem, type IssuePickerProps} from './IssuePicker'
import styles from './RepositoryAndIssuePicker.module.css'
import {
  fetchRepository,
  fetchTopRepositories,
  RepositoryPicker,
  type RepositoryPickerProps,
  type RepositoryPickerRepository,
} from './RepositoryPicker'

export type RepositoryAndIssuePickerProps = Omit<TokenProps, 'leadingVisual' | 'text' | 'ref'> & {
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  onIssueSelection: (issues: IssuePickerItem[]) => void
  organization: string
  defaultRepositoryNameWithOwner: string
  selectedIssueIds?: string[]
  hiddenIssueIds?: string[]
  // pickerType allows this to be treated as a controlled component
  pickerType?: PickerType
  onPickerTypeChange?: (pickerType: PickerType) => void
} & {
  issuePickerProps?: Partial<IssuePickerProps>
  repositoryPickerProps?: Partial<RepositoryPickerProps>
}

export type PickerType = 'Repository' | 'Issue' | null

const REPOSITORY_PICKER_ID = 'repository-picker'
const ISSUE_PICKER_ID = 'issue-picker'

type RepositoryAndIssuePickerInteralProps = {
  pickerType: Exclude<PickerType, null>
  setPickerType: React.Dispatch<React.SetStateAction<PickerType>>
} & Omit<RepositoryAndIssuePickerProps, 'pickerType'>

export function RepositoryAndIssuePicker({
  pickerType: propsPickerType = null,
  anchorElement,
  onPickerTypeChange,
  selectedIssueIds,
  hiddenIssueIds,
  ...rest
}: RepositoryAndIssuePickerProps) {
  const [pickerType, setPickerType] = useState<PickerType>(propsPickerType)

  useEffect(() => {
    setPickerType(propsPickerType)
  }, [propsPickerType])

  useEffect(() => {
    // don't trigger if the internal pickertype is the same as the external
    if (propsPickerType === pickerType) return

    onPickerTypeChange?.(pickerType)

    // eslint-disable-next-line react-hooks/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPickerTypeChange, pickerType])

  if (pickerType === null) {
    return anchorElement({onClick: () => setPickerType('Issue')})
  }

  return (
    <RepositoryAndIssuePickerInternal
      anchorElement={anchorElement}
      selectedIssueIds={selectedIssueIds}
      hiddenIssueIds={hiddenIssueIds}
      {...rest}
      setPickerType={setPickerType}
      pickerType={pickerType}
    />
  )
}

function RepositoryAndIssuePickerInternal({
  anchorElement,
  onIssueSelection,
  organization,
  defaultRepositoryNameWithOwner,
  pickerType,
  setPickerType,
  selectedIssueIds,
  hiddenIssueIds,
  issuePickerProps,
  repositoryPickerProps,
}: RepositoryAndIssuePickerInteralProps) {
  const [currentRepositoryNameWithOwner, setCurrentRepositoryNameWithOwner] =
    useState<string>(defaultRepositoryNameWithOwner)
  const [initialRepository, setInitialRepository] = useState<RepositoryPickerRepository | undefined>(undefined)
  const [topRepositories, setTopRepositories] = useState<RepositoryPickerRepository[] | null>(null)

  const {updateOpenState} = useItemPickersContext()
  const environment = useRelayEnvironment()

  const onBackClick = useCallback(() => {
    setPickerType('Repository')
  }, [setPickerType])

  const onRepoClose = useCallback(() => {
    // If the picker type is Issue, then we know this component has moved forward to the issue picking stage, so we
    // shouldn't update the picker type to overwrite that
    setPickerType(t => (t === 'Issue' ? 'Issue' : null))
    updateOpenState(REPOSITORY_PICKER_ID, false)
  }, [setPickerType, updateOpenState])

  const onIssueClose = useCallback(() => {
    // If the picker type is Repository, then we know this has changed due to switching back to the repository
    // picker, so we shouldn't update the picker type to overwrite that
    setPickerType(t => (t === 'Repository' ? 'Repository' : null))
    updateOpenState(ISSUE_PICKER_ID, false)
  }, [setPickerType, updateOpenState])

  const setRepository = useCallback(
    (repository: RepositoryPickerRepository | undefined) => {
      if (repository?.nameWithOwner) {
        setCurrentRepositoryNameWithOwner(repository?.nameWithOwner)
      }
      // if there was no newly selected repository/NWO, that means that the already selected repository was "de-selected",
      // but with this dual-picker setup, "deselecting" still means "continue to the next picker with the selected repository"
      if (currentRepositoryNameWithOwner || repository?.nameWithOwner) {
        setPickerType('Issue')
      }
    },
    [setPickerType, currentRepositoryNameWithOwner],
  )

  const localOnIssueSelection = (issues: IssuePickerItem[]) => {
    onIssueSelection(issues)
    setPickerType(null)
  }

  useEffect(() => {
    const fetchRepo = async () => {
      const repos = await fetchTopRepositories(environment, {
        first: 5,
        hasIssuesEnabled: true,
      })
      setTopRepositories(repos)
    }
    fetchRepo()
    // eslint-disable-next-line react-hooks/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    async function fetchData() {
      const [organizationLogin, repositoryName] = currentRepositoryNameWithOwner.split('/')
      if (organizationLogin && repositoryName) {
        const repository = await fetchRepository(environment, organizationLogin, repositoryName)
        if (repository != null) {
          setInitialRepository(repository)
        }
      }
    }
    fetchData()
  }, [currentRepositoryNameWithOwner, environment])

  switch (pickerType) {
    case 'Repository':
      if (!topRepositories) return null

      return (
        <RepositoryPicker
          initialRepository={initialRepository}
          preventDefault
          onSelect={setRepository}
          organization={organization}
          topRepositories={topRepositories}
          anchorElement={anchorElement}
          onClose={onRepoClose}
          triggerOpen
          title="Select a repository"
          pickerId={REPOSITORY_PICKER_ID}
          {...repositoryPickerProps}
        />
      )
    case 'Issue':
      return (
        <IssuePicker
          selectedIssueIds={selectedIssueIds}
          hiddenIssueIds={hiddenIssueIds}
          onIssueSelection={localOnIssueSelection}
          anchorElement={anchorElement}
          repositoryNameWithOwner={currentRepositoryNameWithOwner}
          onClose={onIssueClose}
          title={
            <div className={styles.BackToRepositorySelectionButton}>
              <IconButton
                icon={ArrowLeftIcon}
                variant="invisible"
                data-testid="back-to-repo-selection-button"
                aria-label="Back to repository selection"
                tooltipDirection="n"
                onClick={onBackClick}
              />
              <span>{currentRepositoryNameWithOwner}</span>
            </div>
          }
          triggerOpen
          pickerId={ISSUE_PICKER_ID}
          {...issuePickerProps}
        />
      )
  }
}

try{ RepositoryAndIssuePicker.displayName ||= 'RepositoryAndIssuePicker' } catch {}
try{ RepositoryAndIssuePickerInternal.displayName ||= 'RepositoryAndIssuePickerInternal' } catch {}